import React, { useEffect, useState } from "react";
import Select from "react-select";
import { getTripFueling } from "../../api/apiGetrequest";

const styles = {
  container: { padding: "20px" },
  table: { width: "100%", borderCollapse: "collapse", margin: "20px 0" },
  th: {
    backgroundColor: "#f4f4f4",
    color: "#333",
    fontWeight: "bold",
    padding: "10px",
    border: "1px solid #ddd",
  },
  td: { padding: "10px", border: "1px solid #ddd", textAlign: "center" },
  card: {
    border: "1px solid #ddd",
    borderRadius: "5px",
    padding: "15px",
    margin: "10px 0",
    boxShadow: "0 2px 5px rgba(0,0,0,0.1)",
  },
  cardItem: { margin: "5px 0" },
  totalRow: { fontWeight: "bold", backgroundColor: "#f4f4f4" },
  filtersContainer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "20px",
  },
  dateFilters: { display: "flex", alignItems: "center", gap: "10px" },
  dateInput: {
    padding: "5px",
    borderRadius: "4px",
    border: "1px solid #ddd",
    width: "150px",
  },
  select: { width: "250px" },
};

const TripFuelingTable = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [fuelings, setFuelings] = useState([]);
  const [filteredFuelings, setFilteredFuelings] = useState([]);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [stations, setStations] = useState([]);
  const [selectedStation, setSelectedStation] = useState(null);

  useEffect(() => {
    const end = new Date();
    const start = new Date();
    start.setDate(end.getDate() - 7 * 7); // 7 weeks ago
    setStartDate(start.toISOString().split("T")[0]);
    setEndDate(end.toISOString().split("T")[0]);
  }, []);

  const fetchData = async () => {
    try {
      const { result } = await getTripFueling(0, 0, startDate, endDate);
      setFuelings(result);
      setFilteredFuelings(result);

      // Extract unique station names for dropdown
      const uniqueStations = Array.from(
        new Set(result.map((item) => item.StationName))
      ).map((name) => ({ value: name, label: name }));
      setStations(uniqueStations);
    } catch (error) {
      console.error("Error fetching allocations:", error);
    }
  };

  useEffect(() => {
    if (startDate && endDate) fetchData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate]);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Apply filtering when station is selected
  useEffect(() => {
    if (selectedStation) {
      setFilteredFuelings(
        fuelings.filter((item) => item.StationName === selectedStation.value)
      );
    } else {
      setFilteredFuelings(fuelings);
    }
  }, [selectedStation, fuelings]);

  const formatNumber = (num) =>
    num
      ? new Intl.NumberFormat("en-US", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(num)
      : "0.00";

  const totalFuelAmount = filteredFuelings.reduce(
    (sum, item) => sum + parseFloat(item.FuelAmount || 0),
    0
  );
  const totalFuelLitres = filteredFuelings.reduce(
    (sum, item) => sum + parseFloat(item.FuelLitres || 0),
    0
  );

  return (
    <div style={styles.container}>
      {/* Filters: Station (Left) & Date Filters (Right) */}
      <div style={styles.filtersContainer}>
        <Select
          options={stations}
          value={selectedStation}
          onChange={setSelectedStation}
          placeholder="Select Station"
          isClearable
          styles={{ container: (base) => ({ ...base, ...styles.select }) }}
        />
        <div style={styles.dateFilters}>
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            style={styles.dateInput}
          />
          <input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            style={styles.dateInput}
          />
        </div>
      </div>

      {/* Render Table or Cards */}
      {isMobile ? (
        <div>
          {filteredFuelings.map((item) => (
            <div key={item.TripFuelingID} style={styles.card}>
              <div style={styles.cardItem}>
                <strong>Trip Date:</strong> {item.TripDate.split("T")[0]}
              </div>
              <div style={styles.cardItem}>
                <strong>Customer:</strong> {item.CustomerName}
              </div>
              <div style={styles.cardItem}>
                <strong>Station Name:</strong> {item.StationName}
              </div>
              <div style={styles.cardItem}>
                <strong>Fuel Amount:</strong> {formatNumber(item.FuelAmount)}
              </div>
              <div style={styles.cardItem}>
                <strong>Fuel Litres:</strong> {formatNumber(item.FuelLitres)}
              </div>
            </div>
          ))}

          {/* Total Summary for Cards */}
          <div style={styles.card}>
            <div style={styles.cardItem}>
              <strong>Total Fuel Amount:</strong>{" "}
              {formatNumber(totalFuelAmount)}
            </div>
            <div style={styles.cardItem}>
              <strong>Total Fuel Litres:</strong>{" "}
              {formatNumber(totalFuelLitres)}
            </div>
          </div>
        </div>
      ) : (
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>Trip Date</th>
              <th style={styles.th}>Customer</th>
              <th style={styles.th}>Station Name</th>
              <th style={styles.th}>Fuel Amount</th>
              <th style={styles.th}>Fuel Litres</th>
            </tr>
          </thead>
          <tbody>
            {filteredFuelings.map((item) => (
              <tr key={item.TripFuelingID}>
                <td style={styles.td}>{item.TripDate.split("T")[0]}</td>
                <td style={styles.td}>{item.CustomerName}</td>
                <td style={styles.td}>{item.StationName}</td>
                <td style={styles.td}>{formatNumber(item.FuelAmount)}</td>
                <td style={styles.td}>{formatNumber(item.FuelLitres)}</td>
              </tr>
            ))}

            {/* Total Row */}
            <tr style={styles.totalRow}>
              <td style={styles.td} colSpan={3}>
                Total
              </td>
              <td style={styles.td}>{formatNumber(totalFuelAmount)}</td>
              <td style={styles.td}>{formatNumber(totalFuelLitres)}</td>
            </tr>
          </tbody>
        </table>
      )}
    </div>
  );
};

export default TripFuelingTable;
